//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { Api, LocalDictionaryFactory } from '../../api/api.module'
import { ColumnModel } from '../../api/api.models'

import { Activity } from '../../../models/common/activity'
import { Enums } from '../../../models/common/enums'
import { OpDataTypeInGroup } from '../../../models/core/op-data-type-in-group'
import { OpDistributor } from '../../../models/core/op-distributor'
import { OpSupplier } from '../../../models/core'

import { EventsNames } from '../../../helpers/events-names'
import { CommonHelper } from '../../../helpers/common-helper'
import { TemplateHelper, LocalizationHelper, ConfigHelper, OperatorLayoutSettings } from '../../../helpers'

import { DistributorService, DistributorHierarchyService, DistributorExtendedParametersService, DistributorSupplierService } from '../../../services/distributor'
import { UserSessionService } from '../../../services/user-session'

import { CreateEditInitParams } from '../../common/create-edit-popup/create-edit-popup.controller'
import { Details, DetailsScope, DetailsAccordion, DetailsAccordionSection } from '../../common/common.details'
import { DetailsParametersOptions } from '../../common/details-parameters/details-parameters.directive'
import { DetailsPopupOptions, DetailsOptions } from '../../common/details-popup/details-popup.directive'
import { GridOptions, GridController, GridTypes } from '../../common/grid/grid.controller'
import { TreeListOptions } from '../../common/tree-list/tree-list.controller'
import { ItemSwapperPopupOptions, IItemSwapperPopup, ItemSwapperResult } from '../../../modules/common/item-swapper/'
import { IIMRConfirmationPopup, IMRConfirmationPopupOptions, IMRConfirmationPopupInitializedArgs } from '../../common/confirmation-popup2'
import { GridBridge } from '../../../modules/common/common.interfaces'
import { GridEditController } from '../../../modules/common/grid-edit/grid-edit.controller'


interface DistributorDetailsScope extends DetailsScope {
    id: number
}

export class DistributorDetailsDirectiveController extends Details<DistributorDetailsScope> {

    //#region Consts

    public get DISTRIBUTOR_COMMON_TAB_NAME() { return 'DISTRIBUTOR_COMMON_TAB_NAME' }
    public get DISTRIBUTOR_PARAMS_ACCORDION_NAME() { return 'DISTRIBUTOR_PARAMS_ACCORDION_NAME' }
    public get DISTRIBUTOR_EXTENDED_PARAMS_ACCORDION_NAME() { return 'DISTRIBUTOR_EXTENDED_PARAMS_ACCORDION_NAME' }
    public get DISTRIBUTOR_CHILD_DISTRIBUTORS_ACCORDION_NAME() { return 'DISTRIBUTOR_CHILD_DISTRIBUTORS_ACCORDION_NAME' }
    public get DISTRIBUTOR_SUPPLIERS_ACCORDION_NAME() { return 'DISTRIBUTOR_SUPPLIERS_ACCORDION_NAME' }
    public get DISTRIBUTOR_SUPPLIERS_BRIDGE_NAME() { return 'DDISTRIBUTOR_SUPPLIERS_BRIDGE_NAME' }

    private get DISTRIBUTOR_SPECIFICS_TREE_LIST_NAME() { return 'DistributorDetailsSpecifics' }
    private get DISTRIBUTOR_CHILD_DISTRIBUTORS_GRID_NAME() { return 'DISTRIBUTOR_CHILD_DISTRIBUTORS_GRID_NAME' }
    private get DISTRIBUTOR_SUPPLIERS_GRID_NAME() { return 'DISTRIBUTOR_SUPPLIERS_GRID_NAME' }


    //#endregion


    //#region Members

    public basicParametersOptions: DetailsParametersOptions
    public childDistributorsGridOptions: GridOptions
    public childDistributorsItemSwapperPopupOptions: ItemSwapperPopupOptions<OpDistributor>
    public extendedParametersTreeListOptions: TreeListOptions
    public suppliersGridOptions: GridOptions
    public suppliersGridEditOptions: GridBridge

    private childDistributorsCount: number
    private childDistributorsGrid: GridController<OpDistributor>
    private childDistributorsItemSwapperPopup: IItemSwapperPopup<OpDistributor>
    private distributor: OpDistributor
    private idDistributor: number
    private distributorSuppliersGrid: GridController<OpSupplier>
    private suppliersBridgeController: GridEditController
    private distributorSuppliersCount: number
    private distributorSuppliersBridgePopupIdName: string

    //#endregion


    //#region Ctor

    constructor(
        $scope: DistributorDetailsScope,
        $q: ng.IQService,
        $timeout: ng.ITimeoutService,
        private api: Api,
        localDictionary: LocalDictionaryFactory,
        private distributorService: DistributorService,
        private distributorExtendedParametersService: DistributorExtendedParametersService,
        private distributorHierarchyService: DistributorHierarchyService,
        protected userSessionService: UserSessionService,
        private distributorSupplierService: DistributorSupplierService,
        protected referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $q, null, localDictionary, OperatorLayoutSettings.DistributorPopupTabActive, userSessionService, referenceType)

        this.initializeConfirmationPopup()
        this.setEvents()
        this.setOptions()

        if (this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_LIST)) {
            this.configureTabs(this.getTabs())
        }
    }

    //#endregion


    //#region Events

    private onOpen(options: DetailsOptions): void {
        this.$scope.detailsOpenParams = options

        this.idDistributor = options.referenceObjectId

        this.onClear()
        this.showPopup()
        this.onRefresh()
    }

    private onRefresh(toRefresh: OpDistributor = null): void {
        this.$scope.options.isDataLoaded = false

        if (!toRefresh) {
            this.distributorService
                .getDistributor(this.idDistributor)
                .then((distributor: OpDistributor) => {
                    return $.when(
                        this.initialize(distributor)
                    )
                })
                .fail(() => {
                    this.hidePopup()
                })
                .always(() => {
                    this.$scope.options.isDataLoaded = true
                })
        } else {
            $.when(
                this.initialize(toRefresh)
            )
                .always(() => {
                    this.$scope.options.isDataLoaded = true
                })
        }
    }

    private onEditParams(): void {
        this.$scope.$root.$broadcast(
            EventsNames.INITIALIZE_CREATE_EDIT_POPUP,
            this.distributorService.getCreateEditOptions(this.distributor.idDistributor, this.distributor)
        )
    }

    private onEditExtendedParams(): void {
        const options: CreateEditInitParams = this.distributorService.getCreateEditOptions(this.distributor.idDistributor, this.distributor)

        options.createEditSelectedTabType = Enums.SimaxCreateEditSelectedTabType.ExtendedParameters

        this.$scope.$root.$broadcast(
            EventsNames.INITIALIZE_CREATE_EDIT_POPUP,
            options
        )
    }

    private onEditChildDistributors(): void {
        if (this.childDistributorsItemSwapperPopup != null) {
            this.childDistributorsItemSwapperPopup.itemSwapper.setLeftGridDataSource(() => $.Deferred().resolve(this.distributor.childDistributors).promise())
            this.childDistributorsItemSwapperPopup.itemSwapper.reset()
            this.childDistributorsItemSwapperPopup.show()
        }
    }

    private onClear(): void {
        this.$scope.popupTitle = ''
        this.$scope.showTabs = false

        this.distributor = null
        this.extendedParametersTreeListOptions = null
        this.basicParametersOptions = null
        this.childDistributorsGridOptions = null
        this.childDistributorsGrid = null
        this.childDistributorsCount = 0
        this.distributorSuppliersCount = 0
        this.childDistributorsItemSwapperPopupOptions = null
        this.childDistributorsItemSwapperPopup = null
    }

    private onClose(): void {
        this.hidePopup()
    }

    private onConfirmDelete(): void {
        this.distributorService
            .deleteDistributor(this.distributor.idDistributor)
            .then(() => {
                this.onClose()
            })
    }

    private onDelete(): void {
        this.confirmationPopup.show()
    }

    //#endregion


    //#region Initialize

    private initialize(distributor: OpDistributor): JQueryPromise<void> {
        const def: JQueryDeferred<void> = $.Deferred<void>()

        if (distributor != null && (!this.distributor || this.distributor.idDistributor == distributor.idDistributor)) {
            this.distributor = distributor
            this.$scope.popupTitle = distributor.toString()

            this.$scope.showTabs = true

            $
                .when(
                    this.initializeBasicParameters(distributor),
                    this.initializeExtendedParameters(distributor),
                    this.initializeChildDistributors(distributor),
                    this.initializeSuppliers(distributor)
                )
                .always(() => {
                    def.resolve()
                })
        } else {
            def.resolve()
        }

        return def.promise()
    }

    //#endregion


    //#region Confirmation Popup

    public confirmationPopupOptions: IMRConfirmationPopupOptions
    private confirmationPopup: IIMRConfirmationPopup

    private initializeConfirmationPopup(): void {
        this.confirmationPopupOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),
            content: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName),

            onInitialized: (e: IMRConfirmationPopupInitializedArgs) => {
                this.confirmationPopup = e.component
            },

            onConfirmed: () => {
                this.onConfirmDelete()
            }
        }
    }

    //#endregion


    //#region Parameters

    private initializeBasicParameters(distributor: OpDistributor): JQueryPromise<void> {
        const def: JQueryDeferred<void> = $.Deferred<void>()

        this.distributorService
            .getBasicParametersDataTypeGroup()
            .then((dataTypeInGroup: OpDataTypeInGroup[]) => {
                this.basicParametersOptions = {
                    dataTypes: dataTypeInGroup,
                    referenceType: this.referenceType,
                    referenceObject: distributor,
                    useDynamicValues: true,
                    onDataLoaded: () => {
                        def.resolve()
                    }
                }
            })
            .catch(() => {
                def.reject()
            })

        if (this.isAccordionHidden(this.DISTRIBUTOR_PARAMS_ACCORDION_NAME)) {
            def.resolve()
        }

        if (this.isAccordionCollapsed(this.DISTRIBUTOR_PARAMS_ACCORDION_NAME)) {
            def.resolve()
        }

        return def.promise()
    }

    private initializeExtendedParameters(distributor: OpDistributor): void {
        $
            .when<any>(
                this.distributorExtendedParametersService.getExtendedParametersDataTypeGroup(),
                this.distributorExtendedParametersService.getExtendedParametersColumns()
            )
            .then((dataTypeInGroup: OpDataTypeInGroup[], columns: ColumnModel[]) => {
                this.extendedParametersTreeListOptions = null

                this.$timeout(() => {
                    this.extendedParametersTreeListOptions = this.getDetailedParamsGridOptions(
                        this.api,
                        Details.filterOpObjectFields(distributor, dataTypeInGroup),
                        columns,
                        this.DISTRIBUTOR_SPECIFICS_TREE_LIST_NAME,
                        CommonHelper.localStorageVarForSavedEntity.distributorSaved,
                        false
                    )

                    this.extendedParametersTreeListOptions.allowColumnReordering = !this.userSessionService
                        .getLoggedOperator()
                        .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.DistributorExtendedParameters)
                }, 200)
            })
    }

    //#endregion


    //#region Child Distributors

    private initializeChildDistributors(distributor: OpDistributor): void {
        this.initializeChildDistributorsGrid(distributor)
        this.initializeChildDistributorsItemSwapper(distributor)
    }

    private initializeChildDistributorsGrid(distributor: OpDistributor): void {
        if (this.childDistributorsGrid != null) {
            this.childDistributorsGrid.setDataSource(distributor.childDistributors);
        }
        else {
            this.distributorHierarchyService
                .getChildDistributorGridColumns()
                .then((columns: ColumnModel[]) => {
                    this.childDistributorsGridOptions = {
                        addCustomFilters: true,
                        columnsDictionary: columns,
                        name: this.DISTRIBUTOR_CHILD_DISTRIBUTORS_GRID_NAME,
                        needCustomFilter: true,
                        type: GridTypes.PAGINATED_SMALL,

                        allowColumnReordering: !this.userSessionService
                            .getLoggedOperator()
                            .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.DistributorChildDistributors),

                        buttonsOptions: {
                            enabled: true,
                            isRefreshButonHidden: true
                        },

                        dataSource: distributor.childDistributors,

                        onDataReady: (childDistributors: OpDistributor[]) => {
                            this.onChildDistributorstGridDataReady(childDistributors)
                        },

                        onInitialized: (e: any, grid: GridController<OpDistributor>) => {
                            this.childDistributorsGrid = grid
                        },

                        onRowDoubleClick: (e: any) => {
                            this.openChildDistributorDetails(this.childDistributorsGrid.selectedItemId, this.childDistributorsGrid.pageItemsIds)
                        }
                    }
                });
        }
    }

    public openChildDistributorDetails(selectedItemId: number, pageItemsIds: number[]): void {
        this.distributorService.openDetails(selectedItemId, pageItemsIds)
    }

    private initializeChildDistributorsItemSwapper(distributor: OpDistributor): JQueryPromise<void> {
        return this.distributorHierarchyService
            .getChildDistributorItemSwapperOptions(distributor)
            .then((options: ItemSwapperPopupOptions<OpDistributor>) => {
                options.onSave = (result: ItemSwapperResult<OpDistributor>) => {
                    return this.saveChildDistributors(result)
                }

                options.onInitialized = (component: IItemSwapperPopup<OpDistributor>) => {
                    this.childDistributorsItemSwapperPopup = component
                }

                this.childDistributorsItemSwapperPopupOptions = options
            })
    }

    private onChildDistributorstGridDataReady(childDistributors: OpDistributor[]): void {
        this.childDistributorsCount = _.size(childDistributors)
    }

    private onDistributorSuppliersGridDataReady(suppliers: OpSupplier[]): void {
        this.distributorSuppliersCount = _.size(suppliers)
    }

    private saveChildDistributors(result: ItemSwapperResult<OpDistributor>): JQueryPromise<any> {
        return this.distributorHierarchyService
            .saveChildDistributors(this.distributor, result)
            .then((distributor: OpDistributor) => {
                this.onRefresh(distributor)
            })
    }

    //#endregion

    //#region Suppliers

    private initializeSuppliers(distributor: OpDistributor): void {
        this.initializeSuppliersGridOptions(distributor)
        this.initializeSuppliersGridEditOptions(distributor)
    }

    private initializeSuppliersGridOptions(distributor: OpDistributor): void {
        if (this.distributorSuppliersGrid != null) {
            this.distributorSuppliersGrid.setDataSource(distributor.currentSuppliers);
        }
        else {

            this.distributorSupplierService
                .getDistributorSuppliersColumns()
                .then((columns: ColumnModel[]) => {
                    this.suppliersGridOptions = {
                        addCustomFilters: true,
                        columnsDictionary: columns,
                        name: this.DISTRIBUTOR_SUPPLIERS_GRID_NAME,
                        needCustomFilter: true,
                        type: GridTypes.PAGINATED_SMALL,
                        idReferenceType: Enums.ReferenceType.IdSupplier,

                        allowColumnReordering: !this.userSessionService
                            .getLoggedOperator()
                            .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.DistributorSuppliers),
                        buttonsOptions: {
                            enabled: true,
                            isRefreshButonHidden: true
                        },


                        dataSource: distributor.currentSuppliers,

                        onDataReady: (suppliers: OpSupplier[]) => {
                            this.onDistributorSuppliersGridDataReady(suppliers)
                        },

                        onInitialized: (e: any, grid: GridController<OpSupplier>) => {
                            this.distributorSuppliersGrid = grid
                        },

                        onDisposed: () => {
                            this.distributorSuppliersGrid = null
                        }
                    }
                })
        }
    }

    private initializeSuppliersGridEditOptions(distributor: OpDistributor): void {
        this.distributorSuppliersBridgePopupIdName = 'distributorSuppliersBridgePopupIdName'

        this.distributorSupplierService
            .getDistributorSuppliersColumns()
            .then((columns: ColumnModel[]) => {
                this.suppliersGridEditOptions = {
                    columns: columns,
                    id: this.idDistributor,
                    items: distributor.currentSuppliers || [],
                    name: this.DISTRIBUTOR_SUPPLIERS_BRIDGE_NAME,
                    popupId: this.distributorSuppliersBridgePopupIdName,
                    title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SuppliersEdit),
                    isLoadingIndicatorDisabled: true,

                    allowColumnReordering: !this.userSession
                        .getLoggedOperator()
                        .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.DistributorSuppliers),

                    getAvailableDataSourceMethod: this.userSession.getDataProvider().Supplier.getSupplierFilter,

                    onControllerInitialized: (controller: GridEditController) => { this.suppliersBridgeController = controller },
                    onApply: (added: any[], removed: any[], inGrid: any[]) => {
                        return this.onDistributorSupplierApply(added, removed, inGrid)
                    }
                }
            })
    }

    private onEditDistributorSuppliers(): void {
        this.$scope.$broadcast(EventsNames.OPEN_GRID_EDIT_POPUP + this.distributorSuppliersBridgePopupIdName, 'distributorSuppliers')
    }

    private onDistributorSupplierApply(added: any[], removed: any[], inGrid: any[]): JQueryPromise<boolean> {
        const deferred: JQueryDeferred<boolean> = $.Deferred<boolean>()

        this.suppliersBridgeController.setIsLoading(true)

        this.distributorSupplierService
            .onSaveDistributorSuppliers(this.distributor, added, removed, inGrid)
            .then((savedDistributor: OpDistributor) => {
                this.onRefresh(savedDistributor)
                deferred.resolve(true)
            })
            .always(() => {
                this.suppliersBridgeController.setIsLoading(false)
            })

        return deferred.promise()
    }

    //#endregion


    //#region Setup

    public getTabs(): any[] {
        const tabs = []

        if (
            !this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DistributorParameters) ||
            !this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DistributorExtendedParameters) ||
            !this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DistributorChildDistributors)
        ) {
            tabs.push({
                template: TemplateHelper.getIconWithText(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Common), ConfigHelper.Icon.HOME, this.userSessionService),

                name: this.DISTRIBUTOR_COMMON_TAB_NAME,
            })

        }
        return tabs
    }

    public getAccordions(): DetailsAccordion[] {
        const accordions: DetailsAccordion[] = []

        if (!this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DistributorParameters)) {

            const items: DetailsAccordionSection[] = []

            items.push({
                canEdit: this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_EDIT)
                    && !this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DistributorParameters),

                name: this.DISTRIBUTOR_PARAMS_ACCORDION_NAME,
                showInTab: this.DISTRIBUTOR_COMMON_TAB_NAME,
                showStatus: false,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BasicParameters),
                visible: true,

                editMethod: () => {
                    if (this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_EDIT)
                        && !this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DistributorParameters)) {

                        this.onEditParams()
                    }
                },
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.DistributorDetailsAccordions, 0, items))
        }

        if (!this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DistributorExtendedParameters)) {

            const items: DetailsAccordionSection[] = []

            items.push({
                canEdit: this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_EDIT)
                    && !this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DistributorExtendedParameters),

                name: this.DISTRIBUTOR_EXTENDED_PARAMS_ACCORDION_NAME,
                showInTab: this.DISTRIBUTOR_COMMON_TAB_NAME,
                showStatus: false,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SpecificParameters),
                visible: true,

                editMethod: () => {
                    if (this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_EDIT)
                        && !this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DistributorExtendedParameters)) {

                        this.onEditExtendedParams()
                    }
                },
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.DistributorDetailsAccordions, 1, items))
        }


        if (!this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DistributorChildDistributors) ||
            !this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DistributorSuppliers, true)) {

            const items: DetailsAccordionSection[] = []

            if (!this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DistributorChildDistributors)) {

                items.push({
                    canEdit: this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_EDIT)
                        && !this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DistributorChildDistributors),

                    name: this.DISTRIBUTOR_CHILD_DISTRIBUTORS_ACCORDION_NAME,
                    showInTab: this.DISTRIBUTOR_COMMON_TAB_NAME,
                    showStatus: true,
                    title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CHILDREN, this.moduleName),
                    visible: true,

                    editMethod: () => {
                        this.onEditChildDistributors()
                    },

                    status: () => `${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AssignedCount)}${this.distributorSuppliersCount}`
                })
            }

            if (!this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DistributorSuppliers, true)) {

                items.push({
                    canEdit: this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_EDIT)
                        && !this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.DistributorSuppliers, true),

                    name: this.DISTRIBUTOR_SUPPLIERS_ACCORDION_NAME,
                    showInTab: this.DISTRIBUTOR_COMMON_TAB_NAME,
                    showStatus: true,
                    title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SUPPLIERS, this.moduleName),
                    visible: true,

                    editMethod: () => {
                        this.onEditDistributorSuppliers()
                    },

                    status: () => `${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AssignedCount)}${_.size(this.distributor.currentSuppliers)}`
                })
            }

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.DistributorDetailsAccordions, 2, items))
        }

        return accordions
    }

    private setEvents(): void {
        this.$scope.$on(EventsNames.DISTRIBUTOR_OPEN_DETAILS_POPUP + this.$scope.id, (event: ng.IAngularEvent, ...args: any[]) => { this.onOpen(args[3]) })
        this.$scope.$on(EventsNames.DISTRIBUTOR_CLOSE_DETAILS_POPUP + this.$scope.id, () => { this.onClose() })
        this.$scope.$on(EventsNames.CLOSE_CREATE_EDIT_POPUP, (event: ng.IAngularEvent, ...args: any[]) => { this.onRefresh(args[0]) })
    }

    private setOptions(): void {
        this.$scope.popupFor = CommonHelper.distributor
        this.accordionsNameToShow = this.DISTRIBUTOR_COMMON_TAB_NAME

        this.$scope.options = <DetailsPopupOptions>{
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DETAILS, this.moduleName),
            openEventName: EventsNames.DISTRIBUTOR_OPEN_DETAILS_POPUP,
            closeEventName: EventsNames.DISTRIBUTOR_CLOSE_DETAILS_POPUP,
            delHasPermissions: this.userSessionService.getLoggedOperator().hasPermission(Activity.DISTRIBUTOR_DEL) ? Activity[Activity.DISTRIBUTOR_DEL] : null,
            id: this.$scope.id,

            deleteMethod: () => this.onDelete(),
            getAccordionDataMethod: () => { return this.getAccordionData() },
            getAccordionSelectionMethod: () => { return this.getAccordionSelection() },
            updateAccordionMethod: (names: any[]) => { return this.updateAccordions(names) }
        }
    }

    //#endregion


    resetData() { this.onClear() }//DO USUNIECIA!!! - zastąpić onClear
    loadData(id: number, objects: any[]) { this.onOpen(null) }//DO USUNIECIA!!! - zastąpić onOpen
    updateData(result: any) { this.onRefresh(result) }//DO USUNIECIA!!! - zastąpić onRefresh
}